import type {
  NavigationStepMatchMode,
  Recording,
  RecordingVisibility,
  SdkTheme,
  SelectorInfo,
  StepSkipCondition,
} from "@flowr/sdk-core";
import type {
  RecorderPanelAuthState,
  RecorderPanelHandle,
  RecorderPanelHandlers,
  RecorderPanelState,
} from "@flowr/sdk-ui/recorder-panel";
import type { RecorderKernelSnapshot } from "./recorder-kernel";

export type RecorderKernelPanelAuthAdapter = {
  getState: () => RecorderPanelAuthState | undefined;
  onSendCode?: (email: string) => void | Promise<unknown>;
  onVerifyCode?: (code: string) => void | Promise<unknown>;
  onUseDifferentEmail?: () => void | Promise<unknown>;
  onSignOut?: () => void | Promise<unknown>;
};

export type RecorderKernelPanelBinding = {
  render: () => void;
  destroy: () => void;
};

type RecorderKernelPanelApi = {
  panelRoot: HTMLElement;
  subscribe: (listener: (snapshot: RecorderKernelSnapshot) => void) => () => void;
  getSnapshot: () => RecorderKernelSnapshot;
  startRecording: (opts?: {
    title?: string;
    visibility?: RecordingVisibility;
  }) => Promise<Recording>;
  stopRecording: () => Promise<Recording | null>;
  saveCurrentRecording: () => Promise<Recording | null>;
  discardCurrentRecording: () => void;
  getCurrentRecording: () => Recording | null;
  replay: (idOrRecording: string | Recording) => Promise<void>;
  stopReplay: () => void;
  setCaptureScreenshots: (value: boolean) => void;
  bindDraftTitle: (value: string) => void;
  commitTitle: () => void;
  setVisibility: (visibility: RecordingVisibility) => Recording | null;
  updateStepInstruction: (stepId: string, value: string) => void;
  commitStepInstruction: () => void;
  deleteStep: (stepId: string) => void;
  updateStepNavigationMatchMode: (stepId: string, mode: NavigationStepMatchMode) => void;
  updateStepSkipCondition: (stepId: string, condition: StepSkipCondition | null) => void;
  pickElement: (onPicked: (selector: SelectorInfo | null) => void) => void;
  openLibraryView: () => void;
  reloadLibrary: () => Promise<void>;
  closeLibraryView: () => void;
  loadRecording: (id: string) => Promise<Recording>;
  deleteRecording: (id: string) => Promise<void>;
};

export type AttachRecorderPanelBindingOptions = {
  kernel: RecorderKernelPanelApi;
  root?: HTMLElement;
  enableElementPicker?: boolean;
  auth?: RecorderKernelPanelAuthAdapter;
  theme?: SdkTheme;
};

const buildRecorderPanelState = (
  snapshot: RecorderKernelSnapshot,
  authState?: RecorderPanelAuthState,
): RecorderPanelState => ({
  recording: snapshot.current,
  status: snapshot.status,
  draftTitle: snapshot.draftTitle,
  draftVisibility: snapshot.draftVisibility,
  captureScreenshots: snapshot.captureScreenshots,
  replayIndex: snapshot.replayIndex,
  defaultTitle: snapshot.defaultTitle,
  hasUnsavedChanges: snapshot.hasUnsavedChanges,
  canSave: snapshot.canSave,
  view: snapshot.panelView,
  recordings: snapshot.panelView === "library" ? snapshot.libraryRecordings : undefined,
  libraryStatus: snapshot.libraryStatus,
  ...(authState ? { auth: authState } : {}),
});

const buildRenderSignature = (
  snapshot: RecorderKernelSnapshot,
  authState?: RecorderPanelAuthState,
): string => {
  const current = snapshot.current;
  const currentStepsKey =
    current?.steps
      .map((step) => {
        const screenshotKey = step.screenshotDataUrl
          ? `data:${step.screenshotDataUrl.length}`
          : (step.screenshotUrl ?? "none");
        return `${step.id}:${step.kind}:${screenshotKey}`;
      })
      .join(",") ?? "none";
  const currentKey = current
    ? [
        current.id,
        current.title,
        current.updatedAt,
        current.visibility,
        current.steps.length,
        currentStepsKey,
      ].join("|")
    : "none";
  const recordingsKey = snapshot.libraryRecordings
    .map((recording) => `${recording.id}:${recording.updatedAt}:${recording.steps.length}`)
    .join(",");
  const authKey = authState
    ? [
        authState.isAuthenticated,
        authState.pendingEmail ?? "",
        authState.signInError ?? "",
        authState.sessionEmail ?? "",
        authState.canSignOut ?? false,
      ].join("|")
    : "no-auth";

  return [
    currentKey,
    snapshot.status,
    snapshot.draftTitle,
    snapshot.draftVisibility,
    snapshot.captureScreenshots,
    snapshot.replayIndex,
    snapshot.defaultTitle,
    snapshot.hasUnsavedChanges,
    snapshot.canSave,
    snapshot.panelView,
    recordingsKey,
    snapshot.libraryStatus,
    authKey,
  ].join("::");
};

export const attachRecorderPanelBinding = ({
  kernel,
  root,
  enableElementPicker = false,
  auth,
  theme,
}: AttachRecorderPanelBindingOptions): RecorderKernelPanelBinding => {
  const panelRoot = root ?? kernel.panelRoot;
  const handlers: RecorderPanelHandlers = {
    onSendCode: auth?.onSendCode,
    onVerifyCode: auth?.onVerifyCode,
    onUseDifferentEmail: auth?.onUseDifferentEmail,
    onSignOut: auth?.onSignOut,
    onStart: () =>
      kernel.startRecording({
        title: kernel.getSnapshot().draftTitle,
        visibility: kernel.getSnapshot().draftVisibility,
      }),
    onStop: () => kernel.stopRecording(),
    onReplay: () => {
      const current = kernel.getCurrentRecording();
      if (current) {
        return kernel.replay(current);
      }
    },
    onStopReplay: () => {
      kernel.stopReplay();
    },
    onCaptureScreenshotsChange: (value) => {
      kernel.setCaptureScreenshots(value);
    },
    onSave: () => kernel.saveCurrentRecording(),
    onDiscard: () => {
      kernel.discardCurrentRecording();
    },
    onTitleInput: (value) => {
      kernel.bindDraftTitle(value);
    },
    onTitleCommit: () => {
      kernel.commitTitle();
    },
    onVisibilityChange: (value) => {
      kernel.setVisibility(value);
    },
    onStepInstructionInput: (stepId, value) => {
      kernel.updateStepInstruction(stepId, value);
    },
    onStepInstructionCommit: () => {
      kernel.commitStepInstruction();
    },
    onStepDelete: (stepId) => {
      kernel.deleteStep(stepId);
    },
    onStepNavigationMatchModeChange: (stepId, mode) => {
      kernel.updateStepNavigationMatchMode(stepId, mode);
    },
    onStepSkipConditionChange: (stepId, condition) => {
      kernel.updateStepSkipCondition(stepId, condition);
    },
    onPickElement: enableElementPicker
      ? (onPicked) => {
          kernel.pickElement(onPicked);
        }
      : undefined,
    onOpenLibrary: () => {
      kernel.openLibraryView();
    },
    onReloadLibrary: () => kernel.reloadLibrary(),
    onCloseLibrary: () => {
      kernel.closeLibraryView();
    },
    onLoadRecording: (id) => kernel.loadRecording(id).then(() => undefined),
    onDeleteRecording: (id) => kernel.deleteRecording(id),
    onReplayRecording: (id) => kernel.replay(id),
  };

  let lastRenderSignature: string | null = null;
  let pendingState: RecorderPanelState | null = null;
  let panel: RecorderPanelHandle | null = null;
  let panelLoadPromise: Promise<void> | null = null;
  let isDestroyed = false;

  const loadPanel = async (): Promise<void> => {
    const { createRecorderPanel } = await import("@flowr/sdk-ui/recorder-panel");
    if (isDestroyed) {
      return;
    }

    panel = createRecorderPanel({
      root: panelRoot,
      theme,
      handlers,
    });
    if (pendingState) {
      panel.render(pendingState);
    }
  };

  const ensurePanel = (): void => {
    if (panel || panelLoadPromise || isDestroyed) {
      return;
    }
    panelRoot.replaceChildren();
    panelLoadPromise = loadPanel().catch((error) => {
      panelLoadPromise = null;
      lastRenderSignature = null;
      if (!isDestroyed) {
        panelRoot.textContent = "Unable to load FlowR recorder UI.";
      }
      void error;
    });
  };

  const render = (): void => {
    if (isDestroyed) {
      return;
    }
    const snapshot = kernel.getSnapshot();
    if (snapshot.pendingOperation !== null) {
      return;
    }
    const authState = auth?.getState();
    const nextSignature = buildRenderSignature(snapshot, authState);
    if (nextSignature === lastRenderSignature) {
      return;
    }
    lastRenderSignature = nextSignature;
    pendingState = buildRecorderPanelState(snapshot, authState);
    if (panel) {
      panel.render(pendingState);
      return;
    }
    ensurePanel();
  };

  const detach = kernel.subscribe(() => {
    render();
  });

  render();

  return {
    render,
    destroy: () => {
      isDestroyed = true;
      detach();
      panel?.destroy();
      panel = null;
      panelLoadPromise = null;
      pendingState = null;
    },
  };
};
